import React, { useEffect, useState } from 'react';

type Evaluation = {
  scheduled_session_id: string;
  participant_id: string;
  evaluation_id: string;
  reassigned: boolean;
};

type EvaluationData = {
  [scheduledSessionId: string]: {
    [participantId: string]: {
      [evaluationId: string]: {
        reassigned: boolean;
      };
    };
  };
};

type ReassignmentsContextType = {
  completedEvaluations: EvaluationData;
  fetchCompletedEvaluations: (
    scheduledSessionId: string,
    participantId: string,
  ) => void;
  updateReassignments: (
    scheduledSessionId: string,
    participantId: string,
    evaluationId: string,
    reassigned: boolean,
  ) => void;
  useReassignmentUpdateListener: () => string | null;
};

const ReassignmentsContext = React.createContext<
  ReassignmentsContextType | undefined
>(undefined);

type ReassignmentsProviderProps = {
  children: React.ReactNode;
};

const ReassignmentsProvider: React.FC<ReassignmentsProviderProps> = ({
  children,
}) => {
  const [completedEvaluations, setCompletedEvaluations] =
    React.useState<EvaluationData>({});
  // Function to fetch reassignments from the database
  const fetchCompletedEvaluations = async (
    scheduledSessionId: string,
    participantId: string,
  ) => {
    try {
      const response = await fetch(
        `/api/evaluations/evaluations?scheduledSessionId=${scheduledSessionId}&participantId=${participantId}`,
      );
      if (!response.ok) throw new Error('Failed to fetch evaluations');

      const data = await response.json();

      const evaluations = data.reduce(
        (acc: EvaluationData, evaluation: Evaluation) => {
          const {
            scheduled_session_id,
            participant_id,
            evaluation_id,
            reassigned,
          } = evaluation;
          if (!acc[scheduled_session_id]) acc[scheduled_session_id] = {};
          if (!acc[scheduled_session_id][participant_id])
            acc[scheduled_session_id][participant_id] = {};
          acc[scheduled_session_id][participant_id][evaluation_id] = {
            reassigned,
          };
          return acc;
        },
        {},
      );

      // console.log("FROM CONTEXT", evaluations);

      setCompletedEvaluations((prevState) => ({
        ...prevState,
        ...evaluations,
      }));
    } catch (error) {
      console.error('Error fetching evaluations:', error);
    }
  };

  const updateReassignments = React.useCallback(
    async (
      scheduledSessionId: string,
      participantId: string,
      evaluationId: string,
      reassigned: boolean,
    ) => {
      try {
        // Fetch existing reassignments for this session & participant
        const existingResponse = await fetch(
          `/api/evaluations/evaluations?scheduledSessionId=${scheduledSessionId}&participantId=${participantId}`,
        );
        if (!existingResponse.ok)
          throw new Error('Failed to fetch existing evaluations');
        const existingEvaluations = await existingResponse.json();

        // Find an evaluation that is already reassigned
        const previouslyReassignedEvaluation = existingEvaluations.find(
          (evaluation: any) => evaluation.reassigned,
        );

        // If another evaluation was reassigned, reset it to false before proceeding
        if (
          previouslyReassignedEvaluation &&
          previouslyReassignedEvaluation.evaluationId !== evaluationId
        ) {
          await fetch('/api/evaluations/evaluations', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scheduledSessionId,
              participantId,
              evaluationId: previouslyReassignedEvaluation.evaluationId,
              reassigned: false,
            }),
          });
        }

        // Update state optimistically
        setCompletedEvaluations((prevState) => ({
          ...prevState,
          [scheduledSessionId]: {
            ...prevState[scheduledSessionId],
            [participantId]: {
              ...prevState[scheduledSessionId]?.[participantId],
              [evaluationId]: { reassigned },
            },
          },
        }));

        // Update the database with the new reassignment
        const response = await fetch('/api/evaluations/evaluations', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheduledSessionId,
            participantId,
            evaluationId,
            reassigned,
          }),
        });

        // console.log("from context", scheduledSessionId, participantId, reassigned);
        // console.log("response", response);

        if (!response.ok)
          throw new Error('Failed to update reassignments in the database');

        // Fetch latest completed evaluations after update
        await fetchCompletedEvaluations(scheduledSessionId, participantId);

        // Store update flag in localStorage
        localStorage.setItem('reassignmentUpdateFlag', Date.now().toString());
      } catch (error) {
        console.error('Error updating reassignments:', error);
      }
    },
    [],
  );

  const useReassignmentUpdateListener = () => {
    const [flag, setFlag] = useState(
      localStorage.getItem('reassignmentUpdateFlag'),
    );

    useEffect(() => {
      const handleStorageChange = (event: StorageEvent) => {
        if (event.key === 'reassignmentUpdateFlag') {
          setFlag(event.newValue);
        }
      };

      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    return flag;
  };

  return (
    <ReassignmentsContext.Provider
      value={{
        completedEvaluations,
        updateReassignments,
        fetchCompletedEvaluations,
        useReassignmentUpdateListener,
      }}
    >
      {children}
    </ReassignmentsContext.Provider>
  );
};

const useReassignments = () => {
  const context = React.useContext(ReassignmentsContext);
  if (!context) {
    throw new Error(
      'useReassignments must be used within a ReassignmentsProvider',
    );
  }
  return context;
};

export { ReassignmentsContext, ReassignmentsProvider, useReassignments };
